import tinycolor from 'tinycolor2';
import colorDiff, {closest, furthest, diff, rgb_to_lab} from 'color-diff';
import Color from '~/data/records/Color';
import {hexToRgb} from '~/data/records/Color';
import SpotColor from '~/data/records/SpotColor';
import {isNone} from '~/data/accessors/color';
import {
  DARKER_BROWN_FOR_WOODEN_PRODUCT,
  INK_COLORS,
  LIGHTER_BROWN_FOR_WOODEN_PRODUCT,
  LUMINANCE_THRESHOLD_BROWN_WOODEN_PRODUCT,
  ORDERED_INK_COLORS,
  ORDERED_NAMES_AND_NUMBERS_INK_COLORS
} from '~/constants/InkConstants';
import {Color as MMSColor} from 'mms_client_js';

// takes the difference between two hex colors
// difference is between 0, 100.00000085247008
export function difference(color1, color2) {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);
  const colorDiff = diff(rgb_to_lab(rgb1), rgb_to_lab(rgb2));
  return colorDiff;
}

export function contrasting(color) {
  var tiny = tinycolor(color);
  if (tiny.toHsl().l < 0.5) {
    tiny.lighten(40);
  } else {
    tiny.lighten(-40);
  }
  return new Color(tiny.toRgb());
}

export function invert(color) {
  const contrast = contrasting(color);
  var tiny = tinycolor(contrast);
  tiny.desaturate(tiny.toHsl().s * 100 - 5);
  return new Color(tiny.toRgb());
}

export function getSaturation(color) {
  return tinycolor(color).toHsl().s;
}

export function getBrightness(color) {
  return tinycolor(color).getBrightness();
}

export function relativeLuminance(color) {
  return tinycolor(color).getLuminance();
}

export function isLuminant(color) {
  return relativeLuminance(color) > 0.75;
}

export function isDark(color) {
  return relativeLuminance(color) < 0.15;
}

export function isSaturated(color) {
  return getSaturation(color) > 0.25;
}

export function contrastRatio(firstColor, secondColor) {
  return tinycolor.readability(firstColor, secondColor);
}

export function getBlackwhiteConstrastingColor(color, material) {
  if (material?.toLowerCase().includes('glass')) {
    return INK_COLORS.Black;
  }

  return color
    ? isLuminant(color)
      ? INK_COLORS.Black
      : INK_COLORS.White
    : INK_COLORS.Black;
}

export function isWhite(color) {
  if (!color) {
    return false;
  }
  return isLuminant(color) && !isSaturated(color);
}

export function isSameColor(control, test) {
  const channels = ['red', 'green', 'blue'];
  const bothTransparent = isNone(control) && isNone(test);
  return (
    bothTransparent ||
    channels.every(channel => control[channel] === test[channel])
  );
}

export function closestMatch(color, palette, background) {
  const matchColor = new Color(color);
  const paletteColors = palette.map(c => new Color(c));
  return closest(matchColor, paletteColors, background);
}

export function furthestMatch(anchorColor, palette) {
  const matchColor = new Color(anchorColor);
  const paletteColors = palette.map(c => new Color(c));
  return furthest(matchColor, paletteColors);
}
export function includesColor(array, targetColor) {
  return array.some(color => isSameColor(color, targetColor));
}

export function useLightFontColor(bgColor, dark, light) {
  const darkRatio = contrastRatio(bgColor, dark);
  const lightRatio = contrastRatio(bgColor, light);
  return darkRatio < 2 && lightRatio > darkRatio;
}

export function getAvailableColors(isPersonalization, isTransparentAllowed) {
  let availableColors = isPersonalization
    ? ORDERED_NAMES_AND_NUMBERS_INK_COLORS
    : ORDERED_INK_COLORS;

  if (isTransparentAllowed) {
    availableColors = [INK_COLORS.None].concat(availableColors);
  }

  return availableColors;
}

export function makeSpotColor(color) {
  return new SpotColor({
    original: color,
    palette: color,
    user: color
  });
}

export function transparentSpotColor() {
  return new SpotColor({
    original: INK_COLORS.White,
    palette: INK_COLORS.White,
    user: INK_COLORS.None
  });
}

export function mapSpotColorParts(spotColor, colorMapper) {
  const partKeys = ['original', 'palette', 'user'];

  return partKeys.reduce((acc, part) => {
    acc[part] = colorMapper(spotColor[part]);
    return acc;
  }, {});
}

export function isValidColor(color) {
  const anyColor = new MMSColor({
    rgbs: [
      {
        red: 1,
        green: 2,
        blue: 3
      }
    ]
  });
  return anyColor.isValidRgb(color);
}

export function getClosestCandidateColorByRGB({currentColor, candidateColors}) {
  if (!currentColor || !candidateColors?.length) return null;

  let closerRGBColor = null;
  let smallestDiffCurrentColorRGB = Infinity;

  candidateColors.forEach(color => {
    let currentColorRGB = null;

    if (
      !isNaN(currentColor?.red) &&
      !isNaN(currentColor?.green) &&
      !isNaN(currentColor?.blue)
    ) {
      currentColorRGB = currentColor;
    } else {
      currentColorRGB = currentColor?.rgbs ? currentColor?.rgbs[0] : null;
    }

    const candidateColorRGB = color?.rgbs ? color?.rgbs[0] : null;

    if (currentColorRGB && candidateColorRGB) {
      const colorsDifference = diff(
        rgb_to_lab({
          r: currentColorRGB?.red,
          g: currentColorRGB?.green,
          b: currentColorRGB?.blue
        }),
        rgb_to_lab({
          r: candidateColorRGB?.red,
          g: candidateColorRGB?.green,
          b: candidateColorRGB?.blue
        })
      );

      if (
        color?.id !== currentColor?.id &&
        colorsDifference < smallestDiffCurrentColorRGB
      ) {
        smallestDiffCurrentColorRGB = colorsDifference;
        closerRGBColor = color;
      }
    }
  });

  return closerRGBColor || candidateColors[0];
}

export function getBrownForAnchorColor({anchorColor}) {
  const color = {
    id: anchorColor?.id,
    r: parseInt(anchorColor?.rgbs?.[0]?.red, 10),
    red: parseInt(anchorColor?.rgbs?.[0]?.red, 10),
    g: parseInt(anchorColor?.rgbs?.[0]?.green, 10),
    green: parseInt(anchorColor?.rgbs?.[0]?.green, 10),
    b: parseInt(anchorColor?.rgbs?.[0]?.blue, 10),
    blue: parseInt(anchorColor?.rgbs?.[0]?.blue, 10)
  };

  if (isNaN(color.red) || isNaN(color.green) || isNaN(color.blue)) return null;

  const adaptColorForSearch = color => {
    return {
      id: color?.id,
      color,
      rgbs: [
        {
          red: color?.red,
          green: color?.green,
          blue: color?.blue
        }
      ]
    };
  };

  const productColor = getClosestCandidateColorByRGB({
    currentColor: adaptColorForSearch(color),
    candidateColors: Object.values(INK_COLORS).map(color =>
      adaptColorForSearch(color)
    )
  })?.color;

  if (relativeLuminance(color) < LUMINANCE_THRESHOLD_BROWN_WOODEN_PRODUCT) {
    if (
      productColor?.name &&
      productColor?.name === DARKER_BROWN_FOR_WOODEN_PRODUCT?.name
    ) {
      return INK_COLORS.Black;
    } else {
      return DARKER_BROWN_FOR_WOODEN_PRODUCT;
    }
  } else {
    if (
      productColor?.name &&
      productColor?.name === LIGHTER_BROWN_FOR_WOODEN_PRODUCT?.name
    ) {
      return DARKER_BROWN_FOR_WOODEN_PRODUCT;
    } else {
      return LIGHTER_BROWN_FOR_WOODEN_PRODUCT;
    }
  }
}

function adjustBrightness(rgbColor, value) {
  const color = tinycolor(rgbColor);
  const hsl = color.toHsl();
  hsl.l = Math.max(0, Math.min(1, hsl.l + value));
  return tinycolor(hsl).toRgb();
}

function shouldBeBrighter(baseColor) {
  const LIGHTNESS_THRESHOLD = 0.4;
  const baseLightness = tinycolor(baseColor).toHsl().l;

  return baseLightness < LIGHTNESS_THRESHOLD;
}

export function findClosestColorConsideringBrightness(color, palette) {
  const ADJUSTMENT_CONSTANT = 0.1;
  const colorAdjustmentValue = shouldBeBrighter(color)
    ? ADJUSTMENT_CONSTANT
    : -ADJUSTMENT_CONSTANT;

  const adjustedColor = adjustBrightness(color, colorAdjustmentValue);
  const result = colorDiff.closest(adjustedColor, palette);

  return result || null;
}
